// MUI Imports
import type { Theme } from '@mui/material';

const toggleButton: Theme['components'] = {
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ ownerState }) => ({
        backgroundColor: 'var(--mui-palette-divider)',
        boxSizing: 'content-box',
        height: '28px',
        padding: '2px',
        borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
        gap: '2px',
        display: 'flex',
        ...(ownerState.size === 'small' && {
          borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
        }),
        ...(ownerState.size === 'large' && {
          borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
        }),
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme, ownerState }) => {
        return {
          // Apply contained button base styles
          boxShadow: 'none',
          borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
          margin: '0 !important',
          border: 'none !important',

          // Override MUI's automatic border radius resets with higher specificity
          '&.MuiToggleButtonGroup-firstButton, &.MuiToggleButtonGroup-middleButton, &.MuiToggleButtonGroup-lastButton':
            {
              borderRadius: 'var(--mui-shape-customBorderRadius-sm) !important',
            },

          // Apply contained button padding based on size
          ...(ownerState.size === 'small' && {
            padding: theme.spacing(2, 3.5),
          }),
          ...(ownerState.size === 'medium' && {
            padding: theme.spacing(2, 4.5),
          }),
          ...(ownerState.size === 'large' && {
            padding: theme.spacing(2, 5.5),
            borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
            '&.MuiToggleButtonGroup-firstButton, &.MuiToggleButtonGroup-middleButton, &.MuiToggleButtonGroup-lastButton':
              {
                borderRadius:
                  'var(--mui-shape-customBorderRadius-lg) !important',
              },
          }),

          // Unselected state - apply outlined button styles
          '&:not(.Mui-selected):not(.Mui-disabled)': {
            backgroundColor: 'transparent',
            color: 'var(--mui-palette-text-secondary)',
          },

          // Hover state for unselected
          '&:not(.Mui-selected):not(.Mui-disabled):hover': {
            backgroundColor: 'var(--mui-palette-primary-hover)',
            color: 'var(--mui-palette-primary-main)',
            boxShadow: 'none',
          },

          // Selected state (default surface: paper). Override per call site when needed.
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: 'var(--mui-palette-background-paper)',
            color: 'var(--mui-palette-primary-main)',
            boxShadow: 'none',
          },

          // Focus visible state
          '&.Mui-focusVisible:not(:has(span.MuiTouchRipple-root))': {
            backgroundColor: 'var(--mui-palette-primary-hover)',
            boxShadow: 'none',
          },

          // Disabled state
          '&.Mui-disabled': {
            opacity: 0.45,
            color: 'var(--mui-palette-primary-main)',
            backgroundColor: 'var(--mui-palette-background-paper)',
          },
        };
      },
      sizeSmall: {
        borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
        lineHeight: '16px',
        fontSize: '12px',
        // Override MUI's automatic border radius resets
        '&.MuiToggleButtonGroup-firstButton, &.MuiToggleButtonGroup-middleButton, &.MuiToggleButtonGroup-lastButton':
          {
            borderRadius: 'var(--mui-shape-customBorderRadius-sm) !important',
          },
      },
      sizeLarge: ({ theme }) => ({
        borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
        fontSize: theme.typography.button.fontSize,
        lineHeight: theme.typography.button.lineHeight,
        // Override MUI's automatic border radius resets
        '&.MuiToggleButtonGroup-firstButton, &.MuiToggleButtonGroup-middleButton, &.MuiToggleButtonGroup-lastButton':
          {
            borderRadius: 'var(--mui-shape-customBorderRadius-lg) !important',
          },
      }),
    },
  },
};

export { toggleButton };
